import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, makeStyles, IconButton, Tooltip } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import RefreshIcon from "@material-ui/icons/Refresh";
import { galleryList, DEFAULT_NUM_ITEMS_PER_GALLERY_PAGE } from "../../v2_actions/gallery";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Dropdown } from "../form/Dropdown";
import { Separator } from "../layout/Separator";
import {
  albumDropdownOptions,
  sessionNameDropdownOptions,
  shotAtDateDropdownOptions,
  galleryEventDropdownOptions,
} from "../../v2_actions/dropdown_options";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "93%",
    paddingLeft: "15px",
  },
  grayText: {
    fontWeight: "800",
    color: "rgb(96, 96, 96)",
    paddingLeft: "60px",
    fontSize: "12px",
  },
  filterContainer: {
    alignItems: "baseline",
  },
  dropdownFilterStyle: {
    "& select": {
      height: 16,
    },
  },
  clearFilterButton: {
    padding: 4,
    marginTop: 0,
    color: "#606060",
    "&:hover": {
      color: "#1976d2",
    },
  },
  refreshButton: {
    backgroundColor: "#1976d2",
    color: "white",
    padding: 6,
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
}));

export const SideBarGalleryFilter = ({
  gallery_id,
  isFilterForPhotoArt = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const galleries = useSelector(() => galleryList.getVisibleObjects());

  const albumOptions = useSelector(() =>
    albumDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Album",
    }),
  );

  const sessionNameOptions = useSelector(() =>
    sessionNameDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Name",
    }),
  );
  const shotAtDateOptions = useSelector(() =>
    shotAtDateDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Date",
    }),
  );

  const galleryEventOptions = useSelector(() =>
    galleryEventDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "Event",
    }),
  );

  const defaultValues = {
    album: "any",
    session_name: "any",
    shot_at_date: "any",
    gallery_event: "any",
  };
  const formProps = useForm({ defaultValues: defaultValues });
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = formProps;

  // Watch the gallery_event field to filter session_name options
  const selectedGalleryEvent = useWatch({ control, name: "gallery_event" });

  useEffect(() => {
    // Set default pagination for gallery list (also set in AllGalleries.js before initial fetch)
    dispatch(galleryList.updatePaginationNumItemsPerPage(DEFAULT_NUM_ITEMS_PER_GALLERY_PAGE));

    dispatch(albumDropdownOptions.invalidateList());
    dispatch(shotAtDateDropdownOptions.invalidateList());
    dispatch(galleryEventDropdownOptions.invalidateList());
    dispatch(sessionNameDropdownOptions.invalidateList());

    dispatch(albumDropdownOptions.updatePaginationNumItemsPerPage(1000));
    dispatch(shotAtDateDropdownOptions.updatePaginationNumItemsPerPage(1000));
    dispatch(galleryEventDropdownOptions.updatePaginationNumItemsPerPage(1000));
    dispatch(sessionNameDropdownOptions.updatePaginationNumItemsPerPage(1000));

    dispatch(albumDropdownOptions.fetchListIfNeeded());
    dispatch(shotAtDateDropdownOptions.fetchListIfNeeded());
    dispatch(galleryEventDropdownOptions.fetchListIfNeeded());
    dispatch(sessionNameDropdownOptions.fetchListIfNeeded());
  }, []);

  // Re-fetch session names when gallery_event changes
  useEffect(() => {
    if (selectedGalleryEvent !== undefined) {
      // Reset session_name to "any" when event changes (unless it's initial load)
      setValue("session_name", "any");

      // Fetch session names filtered by the selected gallery event
      const filterParams = selectedGalleryEvent && selectedGalleryEvent !== "any"
        ? { gallery_event: selectedGalleryEvent }
        : {};
      dispatch(sessionNameDropdownOptions.fetchWithFilter(filterParams));
    }
  }, [selectedGalleryEvent, dispatch, setValue]);

  const onFilter = (values) => {
    dispatch(galleryList.updateListFilter(values));
    dispatch(galleryList.fetchListIfNeeded());
  };

  const onFilterChanged = () => {
    handleSubmit(onFilter)();
  };

  const onClearFilters = () => {
    setValue("album", "any");
    setValue("session_name", "any");
    setValue("shot_at_date", "any");
    setValue("gallery_event", "any");
    dispatch(galleryList.clearListFilter());
    dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
  };

  const onRefresh = () => {
    dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
  };

  const onViewMap = () => {
    if (gallery_id) {
      history.push(`/photographer/gallery/${gallery_id}/map`);
    } else {
      history.push(`/photographer/map`);
    }
  };

  const renderAlbumFilter = () => {
    return (
      <Grid item xs={12} md={2}>
        <Separator height={10} />
        <Controller
          name="album"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Album"
              fieldProps={fieldProps}
              options={albumOptions}
              onChange={onFilterChanged}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderShotAtDateFilter = () => {
    return (
      <Grid item xs={6} md={2}>
        <Separator height={10} />
        <Controller
          name="shot_at_date"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Date"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              options={shotAtDateOptions}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderGalleryEventFilter = () => {
    return (
      <Grid item xs={6} md={2}>
        <Separator height={10} />
        <Controller
          name="gallery_event"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Event"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              options={galleryEventOptions}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderSessionNameFilter = () => {
    return (
      <Grid item xs={6} md={2}>
        <Separator height={10} />
        <Controller
          name="session_name"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Name"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              options={sessionNameOptions}
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderGenreFilter = () => {
    return (
      <Grid item xs={6} md={2}>
        <Separator height={10} />
        <Controller
          name="genre_tag"
          control={control}
          render={(fieldProps) => (
            <Dropdown
              label="Genre"
              fieldProps={fieldProps}
              onChange={onFilterChanged}
              // options={ genreNameOptions }
              dropdownFilterStyle={classes.dropdownFilterStyle}
            />
          )}
        />
      </Grid>
    );
  };

  const renderTagFilter = () => {
    return "Tag filter";
  };

  const renderGalleryNameFilter = () => {
    return "GN";
  };

  const renderForm = () => {
    return (
      <Grid container spacing={2} className={classes.filterContainer}>
        <Grid item xs={12} md={2} style={{ alignSelf: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '5px', marginTop: '12px' }}>
            <Tooltip title="Refresh galleries">
              <IconButton
                className={classes.refreshButton}
                size="small"
                onClick={onRefresh}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <span className={classes.grayText} style={{ paddingLeft: '30px' }}>Filter</span>
          </div>
        </Grid>
        {renderShotAtDateFilter()}
        {!isFilterForPhotoArt && renderGalleryEventFilter()}
        {!isFilterForPhotoArt && renderAlbumFilter()}
        {isFilterForPhotoArt && renderGenreFilter()}
        {/* { isFilterForPhotoArt && renderTagFilter() } */}
        {renderSessionNameFilter()}
        <Grid item xs={6} md={1}>
          <Separator height={10} />
          <Tooltip title="Clear filters">
            <IconButton
              className={classes.clearFilterButton}
              onClick={onClearFilters}
              size="small"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
        {/* { isFilterForPhotoArt && renderGalleryNameFilter() } */}
      </Grid>
    );
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(onFilter)}>
      {renderForm()}
    </form>
  );
};
